import React, { useState, useEffect } from 'react';
import '../App.css';

export interface ScheduledJobItem {
  id: string;
  task: string;
  schedule_seconds: number | null;
  schedule_description: string;
  description: string;
}

interface ScheduledJobsProps {
  apiUrl: (path: string) => string;
  getAuthHeaders: (payload?: any) => Record<string, string>;
  safeFetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const ScheduledJobs: React.FC<ScheduledJobsProps> = ({
  apiUrl,
  getAuthHeaders,
  safeFetch = fetch,
}) => {
  const [jobs, setJobs] = useState<ScheduledJobItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await safeFetch(apiUrl('/api/v1/admin/scheduled-jobs'), {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setJobs(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load scheduled jobs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, getAuthHeaders, safeFetch]);

  if (loading) {
    return (
      <div className="scheduled-jobs-page" style={{ padding: 24 }}>
        <h1 style={{ marginBottom: 16 }}>Scheduled Jobs (Cron)</h1>
        <p>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scheduled-jobs-page" style={{ padding: 24 }}>
        <h1 style={{ marginBottom: 16 }}>Scheduled Jobs (Cron)</h1>
        <p style={{ color: '#c62828' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="scheduled-jobs-page" style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 8 }}>Scheduled Jobs (Cron)</h1>
      <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>
        Periodic tasks run by Celery Beat (Reminders service). Includes reminder dispatch, video calls, leave/reschedule reminders, meal plans, and LLM pricing refresh.
      </p>
      <div className="scheduled-jobs-table-wrap" style={{ overflowX: 'auto', border: '1px solid #e6e8ea', borderRadius: 8 }}>
        <table className="scheduled-jobs-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e6e8ea' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Job ID</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Task</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Schedule</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                  No scheduled jobs configured.
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.id} style={{ borderBottom: '1px solid #e6e8ea' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 13 }}>{job.id}</td>
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 13 }}>{job.task}</td>
                  <td style={{ padding: '12px 16px' }}>{job.schedule_description}</td>
                  <td style={{ padding: '12px 16px', color: '#444', maxWidth: 400 }}>{job.description}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduledJobs;
